import { articles } from '@/mocks/articles';

// Real site origin comes from the environment variable defined by the platform.
export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuybr.info';
export const SITE_NAME = 'CSSBuyBR.info';

// Favicon provided as authoritative SEO data (embed_code).
export const FAVICON_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  /** Canonical path with leading and trailing slash, e.g. "/categories/". */
  path: string;
  /** Open Graph type: website, article, etc. */
  type: string;
}

/**
 * Normalize a route path: strip trailing slashes and any language prefix
 * (/pt, /de, /en) so all language variants resolve to a single canonical page.
 */
export function normalizePath(pathname: string): string {
  let p = pathname || '/';
  if (p.length > 1) p = p.replace(/\/+$/, '');
  p = p.replace(/^\/(pt|de|en)(?=\/|$)/, '');
  if (p === '') p = '/';
  return p;
}

const ROUTE_SEO: Record<string, SeoData> = {
  '/': {
    title: 'CSSBuy Spreadsheet: Encontre Produtos, Guias e Recursos de Compra',
    description:
      'Explore um CSSBuy Spreadsheet organizado com produtos, categorias, guias, QC, envio, cupons e informações de segurança para facilitar sua pesquisa.',
    keywords:
      'CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy coupon, CSSBuy buying guide',
    path: '/',
    type: 'website',
  },
  '/cssbuy-spreadsheet': {
    title: 'CSSBuy Spreadsheet: Guia Completo para Encontrar e Comparar Produtos',
    description:
      'Guia completo do CSSBuy Spreadsheet: encontre produtos por categoria, compare opções, entenda QC, envio e custos, e compre com mais confiança.',
    keywords:
      'cssbuy spreadsheet, cssbuy spreadsheet guide, cssbuy product spreadsheet, cssbuy categories',
    path: '/cssbuy-spreadsheet/',
    type: 'article',
  },
  '/categories': {
    title: 'CSSBuy Spreadsheet Categories: Todos os Produtos e Links',
    description:
      'Explore todas as categorias do CSSBuy Spreadsheet: tênis, moletons, camisetas, jaquetas, acessórios, bolsas e muito mais, organizadas em um só lugar.',
    keywords:
      'cssbuy categories, cssbuy spreadsheet links, cssbuy products, cssbuy spreadsheet',
    path: '/categories/',
    type: 'website',
  },
  '/shipping': {
    title: 'CSSBuy Shipping Cost: Guia de Custos, Métodos e Prazos',
    description:
      'Entenda os fatores que influenciam o custo de envio do CSSBuy: peso, volume, destino, rota e métodos, e aprenda a reduzir despesas.',
    keywords:
      'cssbuy shipping cost, cssbuy shipping, cssbuy delivery time, cssbuy volumetric weight',
    path: '/shipping/',
    type: 'article',
  },
  '/reviews': {
    title: 'CSSBuy Review: Análise Independente, Prós e Contras',
    description:
      'Análise independente do CSSBuy: recursos, limitações, prós, contras e o que saber antes de usar o serviço de agente de compra.',
    keywords:
      'cssbuy review, cssbuy legit, cssbuy pros and cons, cssbuy buying agent',
    path: '/reviews/',
    type: 'article',
  },
  '/guides': {
    title: 'CSSBuy Guides & Resources: Guias Práticos de Compra',
    description:
      'Guias práticos para usar o CSSBuy Spreadsheet, entender envio, comparar opções e comprar com confiança.',
    keywords:
      'cssbuy guides, cssbuy buying guide, cssbuy resources, cssbuy spreadsheet guide',
    path: '/guides/',
    type: 'website',
  },
  '/cssbuy-review': {
    title: 'CSSBuy Review: Is It Legit? Features, Pros & Cons',
    description:
      'Análise independente do CSSBuy: descubra se é legítimo, recursos, prós, contras e pontos importantes antes de usar o serviço.',
    keywords: 'cssbuy review, cssbuy legit, cssbuy pros and cons, cssbuy safe',
    path: '/cssbuy-review/',
    type: 'article',
  },
  '/safety': {
    title: 'CSSBuy Safety Guide: Segurança e Boas Práticas',
    description:
      'Proteja-se ao usar agentes de compra: aprenda sobre golpes, links falsos, segurança de pagamento e boas práticas de compra.',
    keywords:
      'cssbuy safety, cssbuy scam warning, cssbuy payment safety, cssbuy account security',
    path: '/safety/',
    type: 'article',
  },
  '/about': {
    title: 'About CSSBuyBR.info: Recurso Independente de Informação',
    description:
      'Conheça o CSSBuyBR.info, um recurso informativo independente sobre CSSBuy Spreadsheet, guias e análises. Sem afiliação com a CSSBuy.',
    keywords: 'about cssbuybr, cssbuy spreadsheet resource, cssbuy independent',
    path: '/about/',
    type: 'website',
  },
  '/disclaimer': {
    title: 'Disclaimer | CSSBuyBR.info',
    description:
      'Aviso legal do CSSBuyBR.info: recurso independente, sem afiliação com a CSSBuy, sem processamento de pagamentos ou venda de produtos.',
    keywords: 'cssbuybr disclaimer, cssbuy independent, cssbuy legal',
    path: '/disclaimer/',
    type: 'website',
  },
  '/privacy': {
    title: 'Privacy Policy | CSSBuyBR.info',
    description:
      'Política de privacidade do CSSBuyBR.info: como tratamos dados, cookies e links de terceiros.',
    keywords: 'cssbuybr privacy, privacy policy',
    path: '/privacy/',
    type: 'website',
  },
  '/terms': {
    title: 'Terms of Service | CSSBuyBR.info',
    description:
      'Termos de serviço do CSSBuyBR.info: condições de uso do site, propriedade intelectual e limitações de responsabilidade.',
    keywords: 'cssbuybr terms, terms of service',
    path: '/terms/',
    type: 'website',
  },
};

export function resolveSeo(pathname: string): SeoData {
  const p = normalizePath(pathname);

  const articleMatch = p.match(/^\/article\/([^/]+)$/);
  if (articleMatch) {
    const slug = articleMatch[1];
    const article = articles.find((a) => a.slug === slug);
    if (article) {
      return {
        title: article.title,
        description: article.description,
        keywords: `cssbuy, cssbuy spreadsheet, cssbuy guide, ${article.slug.replace(/-/g, ' ')}`,
        path: `/article/${slug}/`,
        type: 'article',
      };
    }
  }

  return ROUTE_SEO[p] ?? ROUTE_SEO['/'];
}